import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link as RouterLink } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import {
  connectToSSE,
  createRun,
  type SEMEvent,
  type SSEConnection,
} from '../../platform/chat/api/sseClient';
import { chatMessageEntrypoint } from '../../platform/chat/messageEntrypoint';
import { useListPromptsQuery } from '../../store/api/rpcSlice';
import {
  type BacktestResponse,
  type OnboardingMember,
  useEnqueueBacktestMutation,
  useGetDataStatusQuery,
  useGetMemberOnboardingQuery,
  useLazyGetBacktestJobQuery,
  useListOnboardingMembersQuery,
  useTriggerBragSheetSyncMutation,
  useTriggerCapacitySheetSyncMutation,
} from './api/coachMatchingApi';
import { BACKTEST_METRIC_HELP } from './backtestMetricHelp';
import { formatMemberAnswers, memberDisplayLabel, memberSearchText } from './memberContextFormat';

const PROFILE_SLUG = 'coach-matching';
const PROMPT_SLUG = 'profile.coach-matching.base';

const memberFilterOptions = createFilterOptions<OnboardingMember>({
  stringify: (option) => memberSearchText(option),
  trim: true,
});

/** Format a timestamp as relative time (e.g., "2 hours ago"). */
function formatRelativeTime(isoString: string | null | undefined): string {
  if (!isoString) return 'never';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  return `${diffDays}d ago`;
}

/** Check if a sync timestamp is stale (> 24 hours old). */
function isSyncStale(isoString: string | null | undefined): boolean {
  if (!isoString) return true;
  const date = new Date(isoString);
  const now = new Date();
  return now.getTime() - date.getTime() > 24 * 60 * 60 * 1000;
}

/** Safely convert a value to a displayable string. */
function toDisplayString(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
}

export default function CoachMatchingPage() {
  // --- Data status ---
  const { data: dataStatus } = useGetDataStatusQuery();

  // --- Member selection ---
  const { data: members, isLoading: membersLoading } = useListOnboardingMembersQuery();
  const [triggerBragSheetSync] = useTriggerBragSheetSyncMutation();
  const [triggerCapacitySheetSync] = useTriggerCapacitySheetSyncMutation();
  const [enqueueBacktest] = useEnqueueBacktestMutation();
  const [getBacktestJob] = useLazyGetBacktestJobQuery();
  const [selectedMember, setSelectedMember] = useState<OnboardingMember | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncComplete, setSyncComplete] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [isBacktestRunning, setIsBacktestRunning] = useState(false);
  const [backtestError, setBacktestError] = useState<string | null>(null);
  const [backtestResult, setBacktestResult] = useState<BacktestResponse | null>(null);
  const [activeBacktestJobId, setActiveBacktestJobId] = useState<string | null>(null);

  // Fetch onboarding answers when a member is selected
  const { data: memberDetail, isFetching: memberDetailLoading } = useGetMemberOnboardingQuery(
    selectedMember?.form_response_id ?? '',
    { skip: !selectedMember }
  );

  // --- Prompt editor ---
  const { data: promptsData } = useListPromptsQuery({
    slug: PROMPT_SLUG,
    status: 'published',
  });
  const basePromptText = promptsData?.items?.[0]?.text ?? '';
  const promptVersion = promptsData?.items?.[0]?.version;
  const [promptText, setPromptText] = useState('');
  const [promptDirty, setPromptDirty] = useState(false);
  const [promptEditing, setPromptEditing] = useState(false);
  const [promptExpanded, setPromptExpanded] = useState(false);

  // Initialize prompt text when base prompt loads
  useEffect(() => {
    if (basePromptText && !promptDirty) {
      setPromptText(basePromptText);
    }
  }, [basePromptText, promptDirty]);

  const handleResetPrompt = () => {
    setPromptText(basePromptText);
    setPromptDirty(false);
  };

  // --- Matching execution ---
  const [streamText, setStreamText] = useState('');
  const [toolCalls, setToolCalls] = useState<
    Array<{ name: string; input: string; result: string }>
  >([]);
  const [isRunning, setIsRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const sseRef = useRef<SSEConnection | null>(null);
  const resultsPanelRef = useRef<HTMLDivElement>(null);

  // Auto-scroll results panel when content changes.
  // streamText and toolCalls are deps to trigger scroll on content updates.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps trigger scroll on content change
  useEffect(() => {
    if (resultsPanelRef.current) {
      resultsPanelRef.current.scrollTop = resultsPanelRef.current.scrollHeight;
    }
  }, [streamText, toolCalls]);

  // Clean up SSE connection on unmount
  useEffect(() => {
    return () => {
      sseRef.current?.abort();
      sseRef.current = null;
    };
  }, []);

  const handleRunMatch = useCallback(async () => {
    if (!selectedMember) return;

    setIsRunning(true);
    setStreamText('');
    setToolCalls([]);
    setRunError(null);

    // Abort any existing SSE connection
    sseRef.current?.abort();
    sseRef.current = null;

    try {
      // Determine if prompt was modified
      const ephemeralOverride = promptDirty ? promptText : undefined;

      // Create a stateless run
      const { runId } = await createRun({
        prompt:
          'Match this member to their top 5 coaches. Return top 3 primary matches plus 2 explainability contrast picks using different criteria.',
        profileSlug: PROFILE_SLUG,
        formResponseId: selectedMember.form_response_id,
        promptOverride: ephemeralOverride,
        messageEntrypoint: chatMessageEntrypoint.systemAutomation,
      });

      // Connect to SSE to stream results
      const connection = connectToSSE({
        runId,
        onEvent: (semEvent: SEMEvent) => {
          if (!semEvent.sem || !semEvent.event) return;

          const evt = semEvent.event;
          const type = evt.type;

          switch (type) {
            case 'llm.delta':
              if (evt.delta) {
                setStreamText((prev) => prev + String(evt.delta));
              }
              break;
            case 'llm.final':
              if (evt.text) {
                setStreamText(String(evt.text));
              }
              break;
            case 'tool.start':
              if (evt.name) {
                setToolCalls((prev) => [
                  ...prev,
                  { name: String(evt.name), input: toDisplayString(evt.input), result: '' },
                ]);
              }
              break;
            case 'tool.result':
              if (evt.result != null) {
                setToolCalls((prev) => {
                  if (prev.length === 0) return prev;
                  const updated = [...prev];
                  updated[updated.length - 1] = {
                    ...updated[updated.length - 1],
                    result: toDisplayString(evt.result),
                  };
                  return updated;
                });
              }
              break;
          }
        },
        onTerminal: (terminal) => {
          if (terminal.type === 'failed') {
            const errorMsg = terminal.data?.error;
            setRunError(typeof errorMsg === 'string' ? errorMsg : 'Matching run failed');
          }
          setIsRunning(false);
        },
        onError: (err) => {
          setRunError(err.message || 'SSE connection error');
          setIsRunning(false);
        },
      });
      sseRef.current = connection;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to start matching';
      setRunError(message);
      setIsRunning(false);
    }
  }, [selectedMember, promptDirty, promptText]);

  // Auto-sync stale data on mount. Only syncs sources older than 24h.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once when dataStatus first loads
  useEffect(() => {
    if (!dataStatus || isSyncing || syncComplete) return;

    const bragStale = isSyncStale(dataStatus.brag_sheet.last_synced_at);
    const capStale = isSyncStale(dataStatus.capacity_sheet.last_synced_at);

    if (!bragStale && !capStale) {
      setSyncComplete(true);
      return;
    }

    const runSync = async () => {
      setIsSyncing(true);
      setSyncError(null);
      try {
        const tasks: Promise<unknown>[] = [];
        if (bragStale) tasks.push(triggerBragSheetSync().unwrap());
        if (capStale) tasks.push(triggerCapacitySheetSync().unwrap());
        await Promise.all(tasks);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to sync coach data';
        setSyncError(message);
      } finally {
        setIsSyncing(false);
        setSyncComplete(true);
      }
    };

    void runSync();
  }, [dataStatus]);

  const handleRunBacktest = useCallback(async () => {
    setIsBacktestRunning(true);
    setBacktestError(null);
    setBacktestResult(null);
    setActiveBacktestJobId(null);
    try {
      const job = await enqueueBacktest({ limit: 75 }).unwrap();
      setActiveBacktestJobId(job.job_id);

      while (true) {
        const status = await getBacktestJob(job.job_id, true).unwrap();
        if (status.status === 'completed') {
          if (status.result) {
            setBacktestResult(status.result);
            setBacktestError(null);
          } else {
            setBacktestError('Backtest completed but no result payload was returned');
          }
          setActiveBacktestJobId(null);
          break;
        }
        if (status.status === 'failed') {
          setBacktestError(status.error_message ?? 'Backtest failed');
          setActiveBacktestJobId(null);
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to run backtest';
      setBacktestError(message);
      setActiveBacktestJobId(null);
    } finally {
      setIsBacktestRunning(false);
    }
  }, [enqueueBacktest, getBacktestJob]);

  const memberOptions = useMemo(() => members ?? [], [members]);
  const memberAnswers = useMemo(
    () => formatMemberAnswers(memberDetail?.answers),
    [memberDetail?.answers]
  );

  return (
    <Box sx={{ p: 3, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" component="h1">
          Coach Matching
        </Typography>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mt: 1 }}>
          <Tab label="Run Matching" />
          <Tab label="Backtest" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              {isSyncing && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Syncing coach data from Google Sheets...
                  </Typography>
                </Box>
              )}
              {syncError && (
                <Alert severity="error" sx={{ mb: 1 }} variant="outlined">
                  {syncError}
                </Alert>
              )}
              {dataStatus ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {dataStatus.eligible_coaches} of {dataStatus.total_coaches} coaches eligible
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {dataStatus.excluded_coaches.dnm > 0 && (
                        <Chip
                          label={`${dataStatus.excluded_coaches.dnm} DNM`}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      )}
                      {dataStatus.excluded_coaches.offboarding > 0 && (
                        <Chip
                          label={`${dataStatus.excluded_coaches.offboarding} offboarding`}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      )}
                      {dataStatus.excluded_coaches.non_active > 0 && (
                        <Chip
                          label={`${dataStatus.excluded_coaches.non_active} inactive`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {dataStatus.excluded_coaches.no_capacity > 0 && (
                        <Chip
                          label={`${dataStatus.excluded_coaches.no_capacity} no capacity`}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                    <Typography
                      variant="caption"
                      color={
                        isSyncStale(dataStatus.brag_sheet.last_synced_at)
                          ? 'error'
                          : 'text.secondary'
                      }
                    >
                      Brag sheets: {dataStatus.brag_sheet.synced_count} synced
                      {dataStatus.brag_sheet.missing_count > 0 &&
                        `, ${dataStatus.brag_sheet.missing_count} missing`}
                      {' \u00b7 '}
                      {formatRelativeTime(dataStatus.brag_sheet.last_synced_at)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={
                        isSyncStale(dataStatus.capacity_sheet.last_synced_at)
                          ? 'error'
                          : 'text.secondary'
                      }
                    >
                      Capacity: {dataStatus.capacity_sheet.synced_count} synced
                      {dataStatus.capacity_sheet.missing_count > 0 &&
                        `, ${dataStatus.capacity_sheet.missing_count} missing`}
                      {' \u00b7 '}
                      {formatRelativeTime(dataStatus.capacity_sheet.last_synced_at)}
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Loading data status...
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a member, review/edit the matching prompt, then run the LLM-powered coach
            matching.
          </Typography>
        </>
      )}

      {activeTab === 1 && (
        <Paper sx={{ p: 2, overflow: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="h6">Prompt Backtest</Typography>
            <Button
              size="small"
              variant="contained"
              onClick={handleRunBacktest}
              disabled={isBacktestRunning}
              startIcon={
                isBacktestRunning ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <PlayArrowIcon />
                )
              }
            >
              {isBacktestRunning ? 'Running...' : 'Run Backtest'}
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Loads the currently published coach-matching prompt version on each run and compares
            historical actual pairings.
          </Typography>

          {activeBacktestJobId && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Backtest job <code>{activeBacktestJobId}</code> is running in the background.
            </Alert>
          )}

          {backtestError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {backtestError}
            </Alert>
          )}

          {backtestResult && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Prompt: {backtestResult.prompt.slug} (v{backtestResult.prompt.version})
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Updated: {new Date(backtestResult.prompt.updated_at).toLocaleString()} | Cases:{' '}
                {backtestResult.case_count} | Eligible-now rate:{' '}
                {(backtestResult.eligible_now_rate * 100).toFixed(1)}%
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Paper variant="outlined" sx={{ p: 1.25 }}>
                  <Typography variant="caption" color="text.secondary">
                    Eligible Now
                  </Typography>
                  <Typography variant="h6">{backtestResult.analysis.eligible_now_count}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {Math.round(backtestResult.analysis.eligible_now_rate * 100)}% of historical
                    pairings
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.25 }}>
                  <Typography variant="caption" color="text.secondary">
                    Ineligible Now
                  </Typography>
                  <Typography variant="h6">
                    {backtestResult.analysis.ineligible_now_count}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Inactive: {backtestResult.analysis.ineligible_breakdown.inactive_coach} | DNM:{' '}
                    {backtestResult.analysis.ineligible_breakdown.do_not_match_status} | No cap:{' '}
                    {backtestResult.analysis.ineligible_breakdown.no_capacity}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.25 }}>
                  <Typography variant="caption" color="text.secondary">
                    Prompt Improvement Opportunities
                  </Typography>
                  <Typography variant="h6">{backtestResult.recommendations.length}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Generated from hard-constraint misses in backtest cases
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.25 }}>
                  <Typography variant="caption" color="text.secondary">
                    Prediction Accuracy
                  </Typography>
                  <Typography variant="h6">
                    Top-1 {(backtestResult.prediction.top1_hit_rate * 100).toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Top-5 {(backtestResult.prediction.top5_hit_rate * 100).toFixed(1)}%{' | '}
                    Scored: {backtestResult.prediction.prediction_case_count}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Runnable: {backtestResult.prediction.runnable_cases}
                    </Typography>
                    <Tooltip title={BACKTEST_METRIC_HELP.runnableCases}>
                      <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Linked: {backtestResult.prediction.resolved_via_linked_person}
                    </Typography>
                    <Tooltip title={BACKTEST_METRIC_HELP.resolvedViaLinkedPerson}>
                      <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Email fallback: {backtestResult.prediction.resolved_via_email_fallback}
                    </Typography>
                    <Tooltip title={BACKTEST_METRIC_HELP.resolvedViaEmailFallback}>
                      <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Unresolved: {backtestResult.prediction.unresolved_cases}
                    </Typography>
                    <Tooltip title={BACKTEST_METRIC_HELP.unresolvedCases}>
                      <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                  {backtestResult.prediction.prediction_errors > 0 && (
                    <Typography variant="caption" color="warning.main" display="block">
                      Errors: {backtestResult.prediction.prediction_errors}
                    </Typography>
                  )}
                </Paper>
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                Recommended Prompt Updates
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
                {backtestResult.recommendations.map((rec) => (
                  <Paper key={rec.id} variant="outlined" sx={{ p: 1.25 }}>
                    <Typography variant="body2" fontWeight="bold">
                      [{rec.priority.toUpperCase()}] {rec.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 0.5 }}
                    >
                      {rec.why}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {rec.prompt_guidance}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              <Box
                component="pre"
                sx={{
                  fontSize: '0.72rem',
                  bgcolor: 'grey.50',
                  p: 1,
                  borderRadius: 1,
                  maxHeight: 220,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  m: 0,
                  mb: 1.5,
                }}
              >
                {backtestResult.prompt.text}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {backtestResult.cases.map((c, idx) => (
                  <Paper
                    key={`${c.member_email ?? 'member'}-${idx}`}
                    variant="outlined"
                    sx={{ p: 1.25 }}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      {c.member_name ?? c.member_email ?? 'Unknown member'} {'->'}{' '}
                      {c.actual_coach_name ?? c.actual_coach_email ?? 'Unknown coach'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Matched: {new Date(c.matched_at).toLocaleDateString()} | Active:{' '}
                      {String(c.actual_is_active)} | Eligible now: {String(c.actual_eligible_now)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Remaining capacity: {c.actual_remaining_capacity ?? 'n/a'} | Matching status:{' '}
                      {c.actual_matching_status ?? 'n/a'} | Coach status:{' '}
                      {c.actual_coach_status ?? 'n/a'}
                    </Typography>
                    {(c.predicted_top_coach_person_id || c.prediction_error) && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Predicted top coach: {c.predicted_top_coach_person_id ?? 'n/a'}
                        {' | '}Top-1 hit: {String(Boolean(c.prediction_matched_actual_top1))}
                        {' | '}Top-5 hit: {String(Boolean(c.prediction_matched_actual_top5))}
                        {c.prediction_error ? ` | Error: ${c.prediction_error}` : ''}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Box>
            </>
          )}

          {!backtestResult && !isBacktestRunning && !backtestError && (
            <Typography variant="body2" color="text.secondary">
              Click "Run Backtest" to load current prompt version and historical actual pairings.
            </Typography>
          )}
        </Paper>
      )}

      {activeTab === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0 }}>
          <Paper sx={{ p: 2, flexShrink: 0 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Member Context
            </Typography>
            <Autocomplete
              options={memberOptions}
              loading={membersLoading}
              value={selectedMember}
              onChange={(_, value) => setSelectedMember(value)}
              getOptionLabel={memberDisplayLabel}
              filterOptions={memberFilterOptions}
              isOptionEqualToValue={(option, value) =>
                option.form_response_id === value.form_response_id
              }
              renderOption={(props, option) => (
                <li {...props} key={option.form_response_id}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {option.first_name ?? ''} {option.last_name ?? ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.email ?? 'No email'} — {option.company ?? 'Unknown'}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search by name, email, or company"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {!selectedMember && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                Choose a member to load their onboarding context.
              </Typography>
            )}

            {selectedMember && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2">
                  {selectedMember.first_name} {selectedMember.last_name}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {selectedMember.email ?? 'No email'} — {selectedMember.company ?? 'Unknown'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Submitted: {new Date(selectedMember.submitted_at).toLocaleString()}
                </Typography>

                <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.75 }}>
                  Onboarding Answers
                </Typography>

                {memberDetailLoading && <CircularProgress size={18} />}

                {!memberDetailLoading && !memberDetail && (
                  <Typography variant="body2" color="text.secondary">
                    Onboarding data is unavailable for this member.
                  </Typography>
                )}

                {!memberDetailLoading && memberDetail && (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      maxHeight: 260,
                      overflow: 'auto',
                    }}
                  >
                    {memberAnswers.map((item, idx) => (
                      <Box
                        key={`${item.label}-${idx}`}
                        sx={{
                          p: 1.25,
                          borderBottom: idx === memberAnswers.length - 1 ? 'none' : '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ letterSpacing: 0.3 }}
                        >
                          {item.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        >
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Paper>

          <Accordion
            expanded={promptExpanded}
            onChange={(_, expanded) => setPromptExpanded(expanded)}
            disableGutters
            sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider', flexShrink: 0 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6">
                    Matching Prompt{promptVersion ? ` (v${promptVersion})` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Collapsed by default. Expand to review or edit before running.
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={
                    isRunning ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                    void handleRunMatch();
                  }}
                  disabled={!selectedMember || isRunning || isSyncing}
                >
                  {isRunning ? 'Running...' : 'Run Match'}
                </Button>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                  flexShrink: 0,
                }}
              >
                <Typography
                  component={RouterLink}
                  to="/admin/profiles/coach-matching"
                  variant="caption"
                  color="primary"
                  sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  Edit in Profile Editor
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={promptEditing ? <VisibilityIcon /> : <EditIcon />}
                    onClick={() => setPromptEditing((prev) => !prev)}
                  >
                    {promptEditing ? 'Preview' : 'Edit'}
                  </Button>
                  {promptDirty && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={() => {
                        handleResetPrompt();
                        setPromptEditing(false);
                      }}
                    >
                      Reset
                    </Button>
                  )}
                </Box>
              </Box>

              {promptEditing ? (
                <TextField
                  multiline
                  fullWidth
                  value={promptText}
                  onChange={(e) => {
                    setPromptText(e.target.value);
                    setPromptDirty(true);
                  }}
                  placeholder="Loading prompt..."
                  sx={{
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      lineHeight: 1.5,
                    },
                  }}
                  minRows={12}
                />
              ) : (
                <Box
                  sx={{
                    overflow: 'auto',
                    maxHeight: 360,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    p: 2,
                    fontSize: '0.85rem',
                    lineHeight: 1.6,
                    '& h1, & h2, & h3': { mt: 1.5, mb: 0.5 },
                    '& p': { my: 0.5 },
                    '& ul, & ol': { pl: 2.5, my: 0.5 },
                    '& li': { mb: 0.25 },
                    '& pre': {
                      bgcolor: 'grey.200',
                      p: 1,
                      borderRadius: 1,
                      overflow: 'auto',
                      fontSize: '0.8rem',
                    },
                    '& code': { fontSize: '0.8rem' },
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {promptText || '*Loading prompt...*'}
                  </ReactMarkdown>
                </Box>
              )}

              {promptDirty && (
                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  Ephemeral override — changes apply to this run only.{' '}
                  <Typography
                    component={RouterLink}
                    to="/admin/profiles/coach-matching"
                    variant="caption"
                    color="primary"
                    sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Edit &amp; publish in Profile Editor
                  </Typography>
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>

          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              overflow: 'hidden',
              flex: 1,
              minHeight: 240,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0 }}>
              <Typography variant="h6">Results</Typography>
              {isRunning && <CircularProgress size={16} />}
            </Box>

            {runError && (
              <Alert severity="error" sx={{ mb: 1, flexShrink: 0 }}>
                {runError}
              </Alert>
            )}

            {!streamText && !isRunning && toolCalls.length === 0 && (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Select a member and click "Run Match" to see coach recommendations.
                </Typography>
              </Box>
            )}

            <Box
              ref={resultsPanelRef}
              sx={{
                flex: 1,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                minHeight: 0,
              }}
            >
              {/* Tool calls log */}
              {toolCalls.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Tool Calls ({toolCalls.length})
                  </Typography>
                  {toolCalls.map((tc, idx) => (
                    <Box
                      key={`${tc.name}-${idx}`}
                      sx={{
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        p: 1,
                        mb: 0.5,
                        fontSize: '0.7rem',
                        fontFamily: 'monospace',
                      }}
                    >
                      <Typography variant="caption" fontWeight="bold" display="block">
                        {tc.name}
                      </Typography>
                      {tc.input && (
                        <Box
                          component="pre"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            m: 0,
                            maxHeight: 100,
                            overflow: 'auto',
                          }}
                        >
                          {tc.input}
                        </Box>
                      )}
                      {tc.result && (
                        <Box
                          component="pre"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            m: 0,
                            mt: 0.5,
                            color: 'success.dark',
                            maxHeight: 120,
                            overflow: 'auto',
                          }}
                        >
                          {tc.result}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              {/* Streamed LLM output — rendered as markdown */}
              {(streamText || isRunning) && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    LLM Output
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: 'grey.50',
                      p: 1.5,
                      borderRadius: 1,
                      fontSize: '0.85rem',
                      lineHeight: 1.6,
                      '& h1, & h2, & h3': { mt: 1.5, mb: 0.5 },
                      '& p': { my: 0.5 },
                      '& ul, & ol': { pl: 2.5, my: 0.5 },
                      '& li': { mb: 0.25 },
                      '& pre': {
                        bgcolor: 'grey.100',
                        p: 1,
                        borderRadius: 1,
                        overflow: 'auto',
                        fontSize: '0.8rem',
                      },
                      '& code': { fontSize: '0.8rem' },
                      '& table': { borderCollapse: 'collapse', width: '100%', my: 1 },
                      '& th, & td': {
                        border: '1px solid',
                        borderColor: 'grey.300',
                        px: 1,
                        py: 0.5,
                        fontSize: '0.8rem',
                      },
                      '& th': { bgcolor: 'grey.100', fontWeight: 'bold' },
                    }}
                  >
                    {streamText ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamText}</ReactMarkdown>
                    ) : (
                      isRunning && <CircularProgress size={14} />
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
